import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { missingArtifactOf, type DeckSlide } from '@/utils/api-pb';

interface DeckSlideListProps {
  slides: DeckSlide[];
  /** Index within `slides`, including hidden ones. */
  activeIndex: number;
  onSelect: (index: number) => void;
}

/** The slide's own headline, when it has one — the fastest way to recognise a slide. */
function headlineOf(slide: DeckSlide): string {
  const title = slide.props.title;
  return typeof title === 'string' ? title : '';
}

/**
 * The slide strip beside the preview.
 *
 * Text cards rather than rendered thumbnails: a real thumbnail would mean rendering
 * fourteen more iframes, and at strip width a pitch slide reads as a grey smudge
 * anyway. What the owner actually needs here is which slide is which, which ones the
 * deck is missing material for, and a way to jump to one.
 *
 * Order is changed through the edit chat ("move the team slide to the end"), not by
 * dragging: the ops pipeline already has `move_section`, and every change made that
 * way lands in version history and can be rolled back.
 */
export function DeckSlideList({ slides, activeIndex, onSelect }: DeckSlideListProps) {
  const { t } = useTranslation('builder');

  return (
    <ul className="flex gap-[8px] overflow-x-auto pb-[4px] lg:h-full lg:w-[228px] lg:shrink-0 lg:flex-col lg:overflow-y-auto lg:overflow-x-hidden lg:pb-0 lg:pr-[4px]">
      {slides.map((slide, index) => {
        const missing = missingArtifactOf(slide);
        const isActive = index === activeIndex;

        return (
          <li key={slide.id} className="shrink-0 lg:shrink lg:w-full">
            <button
              type="button"
              onClick={() => onSelect(index)}
              aria-current={isActive ? 'true' : undefined}
              className={cn(
                'flex w-[180px] flex-col gap-[4px] rounded-[16px] border px-[12px] py-[10px] text-left transition-colors lg:w-full',
                isActive
                  ? 'border-[var(--main-brand,#2197f1)] bg-[rgba(33,151,241,0.08)]'
                  : 'border-[var(--grey-accent,#eeeeee)] bg-white hover:bg-[var(--grey-background,#f8f8f8)]',
                !slide.visible && 'opacity-60',
              )}
            >
              <span className="flex items-center gap-[6px]">
                <span className="font-manrope text-[12px] font-bold text-[color:var(--grey-text,#969696)]">
                  {index + 1}
                </span>
                <span className="truncate font-manrope text-[14px] font-semibold text-[color:var(--black-text,#0d0d0d)]">
                  {t(`deck.slides.${slide.type}`)}
                </span>
              </span>

              {headlineOf(slide) && (
                <span className="line-clamp-2 font-segoe text-[12px] text-[color:var(--grey-text,#565858)]">
                  {headlineOf(slide)}
                </span>
              )}

              {/* Два разных «пусто»: слайда нет из-за отсутствующего материала — или
                  владелец сам его спрятал. Первое чинится созданием материала,
                  второе — это осознанный выбор, и путать их не надо. */}
              {missing ? (
                <span className="w-fit rounded-[6px] bg-[var(--error-soft,#ffeded)] px-[6px] py-[1px] font-segoe text-[11px] font-semibold text-[color:var(--error-strong,#b3261e)]">
                  {t('deck.missing.badge')}
                </span>
              ) : (
                !slide.visible && (
                  <span className="w-fit rounded-[6px] bg-[var(--grey-background,#f8f8f8)] px-[6px] py-[1px] font-segoe text-[11px] font-semibold text-[color:var(--grey-text,#565858)]">
                    {t('deck.editor.hidden')}
                  </span>
                )
              )}
            </button>
          </li>
        );
      })}
    </ul>
  );
}
